import { useState, useEffect, FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import './ImmunisationForm.css'
import type {
  Immunisation,
  ImmunisationFormData,
  MethodOfAdministration,
  ScheduledVaccine,
} from '../types/immunisation'
import { VaccineAllergyChecker } from './VaccineAllergyChecker'
import { practiceVaccineService } from '../services/practiceVaccineService'
import { vaccineReferenceService } from '../services/vaccineReferenceService'

interface ImmunisationFormProps {
  patientId?: string
  initialImmunisation?: Immunisation
  scheduledVaccine?: ScheduledVaccine | null
  onSubmit: (data: ImmunisationFormData) => Promise<void> | void
  onCancel: () => void
}

const METHOD_OF_ADMIN_OPTIONS: { value: MethodOfAdministration; label: string }[] = [
  { value: 'IM', label: 'IM (Intramuscular)' },
  { value: 'SC', label: 'SC (Subcutaneous)' },
  { value: 'ID', label: 'ID (Intradermal)' },
  { value: 'Oral', label: 'Oral' },
  { value: 'Nasal', label: 'Nasal' },
  { value: 'Intramuscular', label: 'Intramuscular' },
  { value: 'Subcutaneous', label: 'Subcutaneous' },
  { value: 'Intravenous', label: 'Intravenous' },
]

const COMMON_SITES = [
  'Left deltoid',
  'Right deltoid',
  'Left thigh',
  'Right thigh',
  'Left arm',
  'Right arm',
  'Oral',
  'Nasal',
]

const COMMON_VACCINE_TYPES = [
  // Childhood schedule vaccines
  '6 in 1',
  'MenB',
  'PCV',
  'Rotavirus',
  'MMR',
  'Chickenpox',
  'MenC',
  '4 in 1',
  'HPV9',
  'MenACWY',
  'Tdap',
  // Additional vaccines
  'Flu',
  'Nasal Flu Vaccine',
  'PPV',
  'COVID-19',
  // Other common vaccines
  'Hepatitis B',
  'Hepatitis A',
  'Tetanus',
  'Diphtheria',
  'Polio',
]

export function ImmunisationForm(props: ImmunisationFormProps) {
  const { initialImmunisation, scheduledVaccine, onSubmit, onCancel, patientId: _patientId } = props
  const [date, setDate] = useState('')
  const [type, setType] = useState('')
  const [batchNumber, setBatchNumber] = useState('')
  const [manufacturer, setManufacturer] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [siteGiven, setSiteGiven] = useState('')
  const [methodOfAdmin, setMethodOfAdmin] = useState<MethodOfAdministration>('IM')
  const [doseValue, setDoseValue] = useState('')
  const [doseUnit, setDoseUnit] = useState('ml')
  const [scheduledVaccineId, setScheduledVaccineId] = useState<string | undefined>(undefined)
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Enhanced fields
  const [consentGiven, setConsentGiven] = useState(true)
  const [consentType, setConsentType] = useState<'patient' | 'parent' | 'guardian' | 'proxy'>('patient')
  const [consentDate, setConsentDate] = useState('')
  // objectionToVaccination is derived from !consentGiven, no need for separate state
  const [objectionReason, setObjectionReason] = useState('')
  const [expiredVaccineWarningAcknowledged, setExpiredVaccineWarningAcknowledged] = useState(false)
  const [adverseReactionWarnings, setAdverseReactionWarnings] = useState<string[]>([])
  const [selectedPracticeVaccineId, setSelectedPracticeVaccineId] = useState<string | undefined>(undefined)
  const [recommendedByGuideline, setRecommendedByGuideline] = useState(false)
  const [guidelineReference, setGuidelineReference] = useState('')

  // Fetch practice vaccines for batch selection
  const { data: practiceVaccines } = useQuery({
    queryKey: ['practice-vaccines'],
    queryFn: () => practiceVaccineService.getPracticeVaccines(),
  })

  // Fetch vaccine reference for recommendations
  const { data: vaccineReference } = useQuery({
    queryKey: ['vaccine-reference', type],
    queryFn: () => vaccineReferenceService.getVaccineReference(type),
    enabled: !!type,
  })

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10)
    if (initialImmunisation) {
      setDate(initialImmunisation.date)
      setType(initialImmunisation.type)
      setBatchNumber(initialImmunisation.batchNumber)
      setManufacturer(initialImmunisation.manufacturer)
      setExpiryDate(initialImmunisation.expiryDate)
      setSiteGiven(initialImmunisation.siteGiven || '')
      setMethodOfAdmin(initialImmunisation.methodOfAdmin)
      setDoseValue(initialImmunisation.dose.value.toString())
      setDoseUnit(initialImmunisation.dose.unit)
      setScheduledVaccineId(initialImmunisation.scheduledVaccineId)
      setNotes(initialImmunisation.notes || '')
      // Enhanced fields
      setConsentGiven(initialImmunisation.consentGiven ?? true)
      setConsentType(initialImmunisation.consentType || 'patient')
      setConsentDate(initialImmunisation.consentDate || today)
      // objectionToVaccination is derived from !consentGiven
      setObjectionReason(initialImmunisation.objectionReason || '')
      setExpiredVaccineWarningAcknowledged(initialImmunisation.expiredVaccineWarningAcknowledged || false)
      setAdverseReactionWarnings(initialImmunisation.adverseReactionWarnings || [])
      setRecommendedByGuideline(initialImmunisation.recommendedByGuideline || false)
      setGuidelineReference(initialImmunisation.guidelineReference || '')
    } else if (scheduledVaccine) {
      // Pre-fill from scheduled vaccine
      setDate(today)
      setType(scheduledVaccine.vaccineTypes[0] || '')
      setScheduledVaccineId(scheduledVaccine.scheduleId)
      setMethodOfAdmin('IM') // Default
      setDoseValue('0.5')
      setDoseUnit('ml')
      setConsentDate(today)
      // Check if recommended by guideline
      if (vaccineReference) {
        setRecommendedByGuideline(true)
        setGuidelineReference(vaccineReference.guidelineChapter || '')
      }
    } else {
      setDate(today)
      setMethodOfAdmin('IM')
      setDoseValue('0.5')
      setDoseUnit('ml')
      setConsentDate(today)
    }
  }, [initialImmunisation, scheduledVaccine, vaccineReference])

  // Update recommendation status when vaccine type changes
  useEffect(() => {
    if (vaccineReference && !initialImmunisation) {
      setRecommendedByGuideline(true)
      setGuidelineReference(vaccineReference.guidelineChapter || '')
    }
  }, [vaccineReference, type, initialImmunisation])

  // Check for expired vaccine
  const isExpired = date && expiryDate && new Date(expiryDate) < new Date(date)
  const expiredVaccineUsed = isExpired || false

  // Handle practice vaccine selection
  const handlePracticeVaccineSelect = (vaccineId: string) => {
    const vaccine = practiceVaccines?.items.find((v) => v.vaccineId === vaccineId)
    if (vaccine) {
      setBatchNumber(vaccine.batchNumber)
      setManufacturer(vaccine.manufacturer)
      setExpiryDate(vaccine.expiryDate)
      setSelectedPracticeVaccineId(vaccineId)
    }
  }

  const validateForm = (): string | null => {
    if (!date.trim()) {
      return 'Date is required'
    }
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) {
      return 'Date must be a valid date'
    }
    if (dateObj > new Date()) {
      return 'Date cannot be in the future'
    }

    if (!type.trim()) {
      return 'Vaccine type is required'
    }

    if (!batchNumber.trim()) {
      return 'Batch number is required'
    }
    if (batchNumber.trim().length < 3) {
      return 'Batch number must be at least 3 characters'
    }
    if (!/^[A-Za-z0-9\-_]+$/.test(batchNumber.trim())) {
      return 'Batch number must be alphanumeric (may include hyphens and underscores)'
    }

    if (!manufacturer.trim()) {
      return 'Manufacturer is required'
    }

    if (!expiryDate.trim()) {
      return 'Expiry date is required'
    }
    const expiryDateObj = new Date(expiryDate)
    if (isNaN(expiryDateObj.getTime())) {
      return 'Expiry date must be a valid date'
    }
    if (expiryDateObj <= dateObj) {
      return 'Expiry date must be in the future at the time of administration'
    }

    if (!methodOfAdmin) {
      return 'Method of administration is required'
    }

    if (!doseValue.trim()) {
      return 'Dose value is required'
    }
    const doseValueNum = parseFloat(doseValue)
    if (isNaN(doseValueNum) || doseValueNum <= 0) {
      return 'Dose value must be a positive number'
    }

    if (!doseUnit.trim()) {
      return 'Dose unit is required'
    }

    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    // Validate consent
    if (!consentGiven && !objectionReason.trim()) {
      setError('Objection reason is required when consent is not given')
      return
    }

    // Validate expired vaccine acknowledgment
    if (expiredVaccineUsed && !expiredVaccineWarningAcknowledged) {
      setError('You must acknowledge the expired vaccine warning to proceed')
      return
    }

    const formData: ImmunisationFormData = {
      date: date.trim(),
      type: type.trim(),
      batchNumber: batchNumber.trim(),
      manufacturer: manufacturer.trim(),
      expiryDate: expiryDate.trim(),
      siteGiven: siteGiven.trim() || undefined,
      methodOfAdmin,
      dose: {
        value: parseFloat(doseValue),
        unit: doseUnit.trim(),
      },
      scheduledVaccineId: scheduledVaccineId || undefined,
      practiceVaccineId: selectedPracticeVaccineId || undefined,
      notes: notes.trim() || undefined,
      // Enhanced fields
      consentGiven,
      consentType: consentGiven ? consentType : undefined,
      consentDate: consentGiven ? (consentDate || date.trim()) : undefined,
      objectionToVaccination: !consentGiven,
      objectionReason: !consentGiven ? objectionReason.trim() : undefined,
      expiredVaccineUsed,
      expiredVaccineWarningAcknowledged: expiredVaccineUsed ? expiredVaccineWarningAcknowledged : false,
      adverseReactionChecked: adverseReactionWarnings.length > 0,
      adverseReactionWarnings: adverseReactionWarnings.length > 0 ? adverseReactionWarnings : undefined,
      recommendedByGuideline,
      guidelineReference: guidelineReference || undefined,
    }

    setSubmitting(true)
    try {
      await onSubmit(formData)
    } catch (err: any) {
      setError(err?.message || 'Failed to save immunisation')
    } finally {
      setSubmitting(false)
    }
  }

  const isEditing = !!initialImmunisation

  return (
    <form className="immunisation-form" onSubmit={handleSubmit}>
      <div className="immunisation-form-header">
        <h2>{isEditing ? 'Edit Immunisation' : 'Add Immunisation'}</h2>
        <p className="form-subtitle">
          Record patient immunisation with full clinical details including batch number, manufacturer, and administration details.
        </p>
      </div>

      {error && <div className="form-error">{error}</div>}

      {scheduledVaccine && !isEditing && (
        <div className="form-info-banner">
          <strong>Recording scheduled vaccine:</strong> {scheduledVaccine.age} - {scheduledVaccine.vaccineTypes.join(', ')}
        </div>
      )}

      <div className="form-grid">
        <div className="form-field">
          <label>
            Date given<span className="required">*</span>
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            required
          />
        </div>

        <div className="form-field">
          <label>
            Vaccine type<span className="required">*</span>
          </label>
          <input
            type="text"
            list="vaccine-types"
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="e.g. 6 in 1, MMR, MenB"
            required
          />
          <datalist id="vaccine-types">
            {COMMON_VACCINE_TYPES.map((vt) => (
              <option key={vt} value={vt} />
            ))}
          </datalist>
        </div>

        <div className="form-field">
          <label>
            Batch number<span className="required">*</span>
          </label>
          {practiceVaccines && practiceVaccines.items.length > 0 && type && (
            <>
              <div className="practice-vaccine-label">
                📦 Practice Inventory (select to auto-fill batch, manufacturer, and expiry)
              </div>
              <select
                value={selectedPracticeVaccineId || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    handlePracticeVaccineSelect(e.target.value)
                  } else {
                    setSelectedPracticeVaccineId(undefined)
                  }
                }}
                className="practice-vaccine-select"
              >
                <option value="">-- Select from practice inventory (optional) --</option>
                {practiceVaccines.items
                  .filter((v) => v.isActive && v.vaccineType === type)
                  .map((v) => (
                    <option key={v.vaccineId} value={v.vaccineId}>
                      {v.batchNumber} - {v.manufacturer} - Exp: {v.expiryDate} (Qty: {v.quantity}) {v.location ? `- ${v.location}` : ''}
                    </option>
                  ))}
              </select>
            </>
          )}
          {practiceVaccines && practiceVaccines.items.length > 0 && !type && (
            <div className="practice-vaccine-hint">
              💡 Select a vaccine type above to see available batches from practice inventory
            </div>
          )}
          {practiceVaccines && practiceVaccines.items.length > 0 && type && practiceVaccines.items.filter((v) => v.isActive && v.vaccineType === type).length === 0 && (
            <div className="practice-vaccine-hint warning">
              ⚠️ No matching vaccines in practice inventory for "{type}". Enter batch number manually below.
            </div>
          )}
          {(!practiceVaccines || practiceVaccines.items.length === 0) && (
            <div className="practice-vaccine-hint">
              💡 No practice vaccines in inventory. Enter batch number manually below.
            </div>
          )}
          <div className="batch-input-wrapper">
            <input
              type="text"
              value={batchNumber}
              onChange={(e) => {
                setBatchNumber(e.target.value.toUpperCase())
                setSelectedPracticeVaccineId(undefined)
              }}
              placeholder="e.g. ABC123456"
              pattern="[A-Za-z0-9\-_]+"
              required
              className={selectedPracticeVaccineId ? 'batch-from-inventory' : ''}
            />
            {selectedPracticeVaccineId && (
              <span className="batch-source-indicator" title="Batch selected from practice inventory">
                📦
              </span>
            )}
          </div>
          <p className="field-help">
            {type
              ? 'Alphanumeric, minimum 3 characters. Or select from practice inventory above.'
              : 'Alphanumeric, minimum 3 characters. Select vaccine type first to see practice inventory options.'}
          </p>
        </div>

        <div className="form-field">
          <label>
            Manufacturer<span className="required">*</span>
          </label>
          <input
            type="text"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            placeholder="e.g. GSK, MSD, Pfizer"
            required
          />
        </div>

        <div className="form-field">
          <label>
            Expiry date<span className="required">*</span>
          </label>
          <input
            type="date"
            value={expiryDate}
            onChange={(e) => {
              setExpiryDate(e.target.value)
              setExpiredVaccineWarningAcknowledged(false)
            }}
            min={date || new Date().toISOString().split('T')[0]}
            required
          />
          <p className="field-help">Must be in the future at time of administration</p>
          {expiredVaccineUsed && (
            <div className="expired-vaccine-warning">
              <strong>⚠️ Warning: Vaccine is expired at time of administration</strong>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={expiredVaccineWarningAcknowledged}
                  onChange={(e) => setExpiredVaccineWarningAcknowledged(e.target.checked)}
                />
                I acknowledge that an expired vaccine is being administered
              </label>
            </div>
          )}
        </div>

        <div className="form-field">
          <label>Site given</label>
          <input
            type="text"
            list="sites"
            value={siteGiven}
            onChange={(e) => setSiteGiven(e.target.value)}
            placeholder="e.g. Left deltoid"
          />
          <datalist id="sites">
            {COMMON_SITES.map((site) => (
              <option key={site} value={site} />
            ))}
          </datalist>
        </div>

        <div className="form-field">
          <label>
            Method of administration<span className="required">*</span>
          </label>
          <select
            value={methodOfAdmin}
            onChange={(e) => setMethodOfAdmin(e.target.value as MethodOfAdministration)}
            required
          >
            {METHOD_OF_ADMIN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>
            Dose value<span className="required">*</span>
          </label>
          <input
            type="number"
            step="0.1"
            min="0.01"
            value={doseValue}
            onChange={(e) => setDoseValue(e.target.value)}
            placeholder="0.5"
            required
          />
        </div>

        <div className="form-field">
          <label>
            Dose unit<span className="required">*</span>
          </label>
          <input
            type="text"
            value={doseUnit}
            onChange={(e) => setDoseUnit(e.target.value)}
            placeholder="ml"
            required
          />
        </div>

        <div className="form-field full-width">
          <label>Clinical notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional clinical context or observations"
            rows={3}
          />
        </div>
      </div>

      {/* Allergy Checker */}
      {_patientId && type && (
        <div className="form-section">
          <h3>Allergy Check</h3>
          <VaccineAllergyChecker
            patientId={_patientId}
            vaccineType={type}
            onWarningsChange={setAdverseReactionWarnings}
          />
        </div>
      )}

      {/* NIAC Recommendation Status */}
      {recommendedByGuideline && (
        <div className="form-section">
          <div className="guideline-status">
            <strong>✓ Recommended by NIAC Guidelines</strong>
            {guidelineReference && <span> - {guidelineReference}</span>}
          </div>
        </div>
      )}

      {/* Consent Section */}
      <div className="form-section">
        <h3>Consent & Objection</h3>
        <div className="form-field">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={consentGiven}
              onChange={(e) => {
                setConsentGiven(e.target.checked)
                if (!e.target.checked) {
                  // objectionToVaccination is derived from !consentGiven
                } else {
                  setObjectionReason('')
                }
              }}
            />
            Consent given for vaccination
          </label>
        </div>

        {consentGiven && (
          <>
            <div className="form-field">
              <label>
                Consent type<span className="required">*</span>
              </label>
              <select
                value={consentType}
                onChange={(e) => setConsentType(e.target.value as 'patient' | 'parent' | 'guardian' | 'proxy')}
                required
              >
                <option value="patient">Patient</option>
                <option value="parent">Parent</option>
                <option value="guardian">Guardian</option>
                <option value="proxy">Proxy</option>
              </select>
            </div>

            <div className="form-field">
              <label>
                Consent date<span className="required">*</span>
              </label>
              <input
                type="date"
                value={consentDate}
                onChange={(e) => setConsentDate(e.target.value)}
                max={date || new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </>
        )}

        {!consentGiven && (
          <div className="form-field full-width">
            <label>
              Objection reason<span className="required">*</span>
            </label>
            <textarea
              value={objectionReason}
              onChange={(e) => setObjectionReason(e.target.value)}
              placeholder="Reason for objection to vaccination"
              rows={3}
              required
            />
          </div>
        )}
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Immunisation'}
        </button>
      </div>
    </form>
  )
}
